import { create } from 'zustand';
import Router from 'next/router';
import posthog from 'posthog-js';
import { googleLogout } from '@react-oauth/google';

import {
  getAuth,
  removeAuth,
  isRunningInIframe,
  getCurrentTeamToken,
  setCurrentTeamToken,
  removeCurrentTeamToken,
} from '@services/identity.service';

import {
  generateTeamToken,
  verifyTeamToken,
  getUserAccountById,
  getUserTeams,
  restrictCopilot,
} from '@services/youtube-platform.service';

import type { PurchasePlanSettings } from '@components/PricingPlans';

export interface TeamContext {
  oid: string | null;
  tid?: string;
  name?: string;
  utid?: string;
  role?: string;
}

export interface Team {
  id: string;
  role: string;
  email?: string;
  billing_account: {
    id: string;
    name: string;
    owner_id: string;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: any;
  };
}

export interface TeamUser {
  user_role?: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

const detailRoutes = [
  '/my-teams/[teamId]',
  '/zoom/[platformId]',
  '/youtube/[platformId]',
  '/teams/[platformId]',
  '/ipa/[platformId]',
  '/stats/[streamId]',
  '/streams/[streamId]/prepare',
];

// Cross-tab auth events travel over localStorage rather than BroadcastChannel:
// TeamsGate reads this key via @uidotdev/usehooks's useLocalStorage (same
// pattern as usePptStreamState). `at` is a uniquifier — two logouts in a row
// would otherwise write byte-identical JSON and readers keyed on value-diffing
// wouldn't see the second one.
export const AUTH_STATE_KEY = 'authStateChanged';
export interface AuthStateMessage {
  event: 'login' | 'logout';
  at: number;
}
export const IDLE_AUTH_STATE: AuthStateMessage = { event: 'logout', at: 0 };

export function broadcastAuthState(event: AuthStateMessage['event']) {
  if (typeof window === 'undefined') return;
  const message: AuthStateMessage = { event, at: Date.now() };
  const newValue = JSON.stringify(message);
  window.localStorage.setItem(AUTH_STATE_KEY, newValue);
  // The native `storage` event only fires in *other* tabs — dispatch it
  // manually here too (matching what useLocalStorage's own setter does) so the
  // module-scope logout listener below observes writes from this same tab.
  window.dispatchEvent(new StorageEvent('storage', { key: AUTH_STATE_KEY, newValue }));
}

// Team switches travel on their own channel. A Gamma / Canva deck embeds one
// iframe per slide and each frame runs its own copy of this store, so the frame
// the user switched in is the only one that hears about it. The SA_TEAM cookie
// they all read is shared (same origin, same storage partition), which means the
// sibling frames immediately start sending the new team's token on their next
// API call while still rendering the workspace we just left. Mirroring the
// switch over the channel keeps every frame — and every other tab — in step,
// without each of them minting a redundant team token.
const TEAM_CHANNEL_NAME = 'teamStateChanged';

interface TeamSwitchMessage {
  type: 'team-switch';
  /** Team.id (the user-team row) — the same list is loaded in every frame. */
  utid: string;
}

// One channel object for both send and receive: a BroadcastChannel never
// delivers to itself, so the frame that switched is the only one that skips the
// handler — exactly what we want, it has already applied the switch locally.
let _teamChannel: BroadcastChannel | null = null;
function getTeamBroadcastChannel(): BroadcastChannel | null {
  if (typeof window === 'undefined' || !('BroadcastChannel' in window)) return null;
  if (!_teamChannel) {
    _teamChannel = new window.BroadcastChannel(TEAM_CHANNEL_NAME);
  }
  return _teamChannel;
}

export interface TeamsState {
  // --- state ---
  teams: Team[];
  user: TeamUser | undefined;
  ownTeam: Team | undefined;
  currentTeam: Team | undefined;
  currentTeamBillingAccountOwnerID: string | undefined;
  teamContext: TeamContext;
  teamMessages: string[];
  teamErrors: string[];
  modal: boolean;
  newTeam: Team | undefined;
  isLoading: boolean;
  isClient: boolean;
  celloUcc: string | null;
  /** Synced from usePurchasePlan by TeamsDataProvider */
  settings: PurchasePlanSettings | null;

  // --- pure setters ---
  setTeams: (teams: Team[]) => void;
  setUser: (user: TeamUser | undefined) => void;
  setOwnTeam: (team: Team | undefined) => void;
  setCurrentTeam: (team: Team | undefined) => void;
  setTeamContext: (ctx: TeamContext | undefined) => void;
  setTeamMessages: (msgs: string[]) => void;
  setTeamErrors: (errs: string[]) => void;
  setModal: (open: boolean) => void;
  toggleModal: () => void;
  setNewTeam: (team: Team | undefined) => void;
  setIsLoading: (v: boolean) => void;
  setIsClient: (v: boolean) => void;
  setCelloUcc: (ucc: string | null) => void;
  setSettings: (s: PurchasePlanSettings | null) => void;

  // --- plain actions (no hook deps) ---
  updateTeam: (updatedTeam: Partial<Team['billing_account']> & { id: string }) => void;
  filterTeamsByRole: (role: string, negate?: boolean) => Team[];
  isSelected: (id: string) => boolean;
  isTeamAdmin: () => boolean;
  isTeamOwner: () => boolean;
  isTeamModerator: () => boolean;
  isLoggedIn: () => boolean;
  getBrandLogo: (defaultImageUrl: string) => string;
  isLogoRequired: () => boolean;
  initTeam: () => Promise<void>;
  switchTeam: (team: Team, options?: { broadcast?: boolean }) => void;
  applyTeamSwitch: (team: Team) => void;
  selectTeam: (team: Team) => void;
  doLogout: () => Promise<void>;
  checkIsCopilot: () => Promise<void>;
}

export const useTeamsStore = create<TeamsState>()((set, get) => ({
  teams: [],
  user: undefined,
  ownTeam: undefined,
  currentTeam: undefined,
  currentTeamBillingAccountOwnerID: undefined,
  teamContext: { oid: null },
  teamMessages: [],
  teamErrors: [],
  modal: false,
  newTeam: undefined,
  isLoading: true,
  isClient: false,
  celloUcc: null,
  settings: null,

  // --- setters ---
  setTeams: (teams) => set({ teams }),
  setUser: (user) => set({ user }),
  setOwnTeam: (ownTeam) => set({ ownTeam }),
  setCurrentTeam: (currentTeam) =>
    set({
      currentTeam,
      currentTeamBillingAccountOwnerID: currentTeam?.billing_account?.owner_id,
    }),
  setTeamContext: (teamContext) => set({ teamContext: teamContext ?? { oid: null } }),
  setTeamMessages: (teamMessages) => set({ teamMessages }),
  setTeamErrors: (teamErrors) => set({ teamErrors }),
  setModal: (modal) => set({ modal }),
  toggleModal: () => set((s) => ({ modal: !s.modal })),
  setNewTeam: (newTeam) => set({ newTeam }),
  setIsLoading: (isLoading) => set({ isLoading }),
  setIsClient: (isClient) => set({ isClient }),
  setCelloUcc: (celloUcc) => set({ celloUcc }),
  setSettings: (settings) => set({ settings }),

  // --- plain actions ---
  updateTeam: (updatedTeam) =>
    set((s) => {
      const patch = (t: any) => ({ ...t, billing_account: { ...t.billing_account, ...updatedTeam } });
      const isMatch = (t: any) => t.billing_account.id === updatedTeam.id;
      return {
        teams: s.teams.map((t) => (isMatch(t) ? patch(t) : t)),
        currentTeam: s.currentTeam && isMatch(s.currentTeam) ? patch(s.currentTeam) : s.currentTeam,
      };
    }),

  filterTeamsByRole: (role, negate = false) =>
    get().teams.filter((t) => (negate ? t.role !== role : t.role === role)),

  isSelected: (id) => id === get().currentTeam?.billing_account?.id,
  isTeamAdmin: () => !!get().currentTeam?.role?.match('^(owner|editor)$'),
  isTeamOwner: () => !!get().currentTeam?.role?.match('^(owner)$'),
  isTeamModerator: () => !!get().currentTeam?.role?.match('^(owner|editor|moderator)$'),
  isLoggedIn: () => !!get().user && get().user?.user_role !== 'guest',

  getBrandLogo: (defaultImageUrl) => {
    const billingAccount = get().currentTeam?.billing_account;
    if (billingAccount?.purchase_plan?.custom_branding_allowed) {
      return billingAccount.brand_logo ?? defaultImageUrl;
    }
    return defaultImageUrl;
  },

  isLogoRequired: () => {
    const billingAccount = get().currentTeam?.billing_account;
    if (billingAccount?.purchase_plan?.no_branding_allowed) {
      return billingAccount.is_branding_required !== false;
    }
    return true;
  },

  initTeam: async () => {
    if (!getAuth()) {
      set({ isLoading: false });
      return;
    }

    const [userResponse, teamsResponse] = await Promise.all([
      getUserAccountById(),
      getUserTeams(),
    ]);

    if (userResponse.status && userResponse.entity) {
      set({ user: userResponse.entity });
    }

    const teams: Team[] = teamsResponse.status && teamsResponse.entity
      ? teamsResponse.entity
      : [];

    if (teams.length > 0) {
      set({ teams });

      // Determine which team to activate (mirrors original getCurrentTeam logic)
      const own = teams.find((t: Team) => t.role === 'owner');
      set({ ownTeam: own });

      const teamToken = getCurrentTeamToken();
      let activeTeam: Team | undefined;

      if (teamToken) {
        const teamData = await verifyTeamToken(teamToken);
        if (teamData?.entity) {
          activeTeam = teams.find((t: Team) => t.billing_account.id === teamData.entity.tid);
        }
      }

      const target = activeTeam ?? own;
      // Bootstrap switches stay local — see switchTeam.
      if (target) get().switchTeam(target, { broadcast: false });
      get().checkIsCopilot();
    }

    set({ isLoading: false });
  },

  switchTeam: (team, { broadcast = true } = {}) => {
    const { teamContext } = get();
    if (team.billing_account.owner_id === teamContext?.oid) return;

    generateTeamToken(team.billing_account.id).then(
      (data: { status: boolean; entity: { token: string } }) => {
        if (!data.status) return;
        setCurrentTeamToken(data.entity.token);
        get().applyTeamSwitch(team);
        // Only a switch the user asked for travels: initTeam resolves the same
        // SA_TEAM cookie in every frame, so its bootstrap switch is already
        // agreed on — and a frame that read that cookie a moment before someone
        // switched would announce the workspace we just left back to the deck.
        if (broadcast) {
          const message: TeamSwitchMessage = { type: 'team-switch', utid: team.id };
          getTeamBroadcastChannel()?.postMessage(message);
        }
      }
    );
  },

  // Move this store onto `team` without minting a team token — the frame that
  // started the switch already generated one and wrote it to the shared SA_TEAM
  // cookie, which is what every frame's requests read. Idempotent, so a frame
  // that is already on the team ignores the broadcast instead of re-rendering
  // every subscriber.
  applyTeamSwitch: (team) => {
    if (team.billing_account.owner_id === get().teamContext?.oid) return;
    set({
      teamContext: {
        name: team.billing_account.name,
        tid: team.billing_account.id,
        oid: team.billing_account.owner_id,
        utid: team.id,
        role: team.role,
      },
      currentTeam: team,
      currentTeamBillingAccountOwnerID: team.billing_account.owner_id,
    });
  },

  selectTeam: (team) => {
    const route = detailRoutes.find((r) => r === Router.pathname);
    if (route) {
      set({ newTeam: team, modal: true });
    } else {
      get().switchTeam(team);
    }
  },

  doLogout: async () => {
    try { posthog.capture('Logged Out'); } catch { /* posthog may not be initialised */ }
    googleLogout();
    await removeAuth();
    removeCurrentTeamToken();
    set({ currentTeam: undefined, teamContext: { oid: null } });
    localStorage.removeItem('template_id');
    broadcastAuthState('logout');
  },

  checkIsCopilot: async () => {
    const res = await restrictCopilot(Router.pathname);
    if (res.status) return;
    get().doLogout();
  },
}));

export const useIsLogoRequired = (): boolean =>
  useTeamsStore((s) => {
    const ba = s.currentTeam?.billing_account;
    if (ba?.purchase_plan?.no_branding_allowed) {
      return ba.is_branding_required !== false;
    }
    return true;
  });

export const useBrandLogo = (defaultImageUrl: string): string =>
  useTeamsStore((s) => {
    const ba = s.currentTeam?.billing_account;
    if (ba?.purchase_plan?.custom_branding_allowed) {
      return ba.brand_logo ?? defaultImageUrl;
    }
    return defaultImageUrl;
  });

// ---------------------------------------------------------------------------
// Vanilla cross-tab logout listener (client-only).
// The "login" case (PowerPoint re-auth) is handled by TeamsGate which has
// access to the useOffice hook.
// ---------------------------------------------------------------------------
if (typeof window !== 'undefined') {
  window.addEventListener('storage', async (e: StorageEvent) => {
    if (e.key !== AUTH_STATE_KEY || !e.newValue) return;
    let message: AuthStateMessage;
    try {
      message = JSON.parse(e.newValue);
    } catch {
      return;
    }
    if (message.event !== 'logout') return;

    await removeAuth();
    if (isRunningInIframe() && getAuth()?.token) return;
    removeCurrentTeamToken();
    const { isProtected } = await import('@lib/security');
    if (isProtected(window.location.pathname)) {
      Router.push('/login');
    }
  });
}

// ---------------------------------------------------------------------------
// Cross-frame team switch listener (client-only). Every slide of an embedded
// deck is its own frame with its own store, so a switch made in one of them has
// to be replayed here; see TEAM_CHANNEL_NAME above.
// ---------------------------------------------------------------------------
const teamChannel = getTeamBroadcastChannel();
if (teamChannel) {
  teamChannel.onmessage = (e: MessageEvent<TeamSwitchMessage>) => {
    if (e.data?.type !== 'team-switch') return;

    const state = useTeamsStore.getState();
    const team = state.teams.find((t) => t.id === e.data.utid);
    if (!team) {
      // No teams list to resolve the id against — this frame is still
      // bootstrapping. Re-run initTeam so it reads the SA_TEAM cookie the
      // switching frame has just rewritten. A bootstrap already in flight read
      // the old cookie and may still win the race, in which case this frame
      // stays on the previous workspace until it remounts; it can no longer
      // drag the other frames along with it, since it won't broadcast.
      state.initTeam();
      return;
    }

    state.applyTeamSwitch(team);

    // Detail routes are pinned to one workspace's record, so the page we're on
    // doesn't exist in the one we just moved to — the same reason selectTeam
    // routes them through TeamSwitchWarning instead of switching in place.
    if (detailRoutes.includes(Router.pathname)) Router.push('/');
  };
}
